import { get, kebabCase, startCase } from 'lodash';
import { graphql } from 'gatsby';
import React, { Component } from 'react';
import styled from 'styled-components';
import { A, H2, H3, H5, HR, P, Small } from '@upsolve/ui';

import TopLevelLayout from '../components/Layout/TopLevelLayout';
import SEO from '../components/SEO/SEO';
import MainHeader from '../components/Layout/Header'
import MainFooter from '../components/Layout/Footer'
import { Label } from '../components/Type/Label.label';
import ArticleTile from '../components/Learn/ArticleTile.div';
import trackPageView from '../components/analytics/trackPageView';
import Page from '../components/Layout/Page.div';
import ImageInContext from '../components/Media/ImageInContext';
import authorToJSONLD from '../components/SEO/authorToJSONLD';
import * as CONTENT_CREATION_METHODS from '../components/analytics/contentCreationMethods';
import * as CONTENT_GROUPS from '../components/analytics/contentGroups';

const AuthorProfileHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  .headshot {
    height: 72px;
    width: 72px;
    margin: 1em 0 0.5em;
    border-radius: 36px;
    overflow: hidden;
    img {
      width: 100%;
    }
  }
  small {
    margin-top: 0.5em;
  }
`;

export default class AuthorProfile extends Component {
  componentDidMount() {
    trackPageView({
      contentCreationMethod: CONTENT_CREATION_METHODS.PROGRAMMATIC,
      contentGroup: AUTHOR_PROFILE,
    });
  }

  render() {
    const { author } = this.props.data;

    return (
      <TopLevelLayout>
        <SEO
          title={`${author.name} - ${author.role} | Upsolve`}
          description={get(author, 'biography.biography') || `${author.name} is a contributor for Upsolve.`}
          image={get(author, 'image.image.file.url') || 'https://upsolve.org/images/meta-legalaid.png'}
          url={`https://upsolve.org/authors/${kebabCase(author.name).toLowerCase()}/`}
          jsonLD={[
            {
              '@context': 'http://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@context': 'http://schema.org',
                  '@type': 'ListItem',
                  position: 1,
                  name: 'Learn',
                  item: 'https://upsolve.org/authors/',
                },
                {
                  '@context': 'http://schema.org',
                  '@type': 'ListItem',
                  position: 2,
                  name: author.name,
                  item: `https://upsolve.org/authors/${kebabCase(author.name).toLowerCase()}/`,
                },
              ],
            },
            authorToJSONLD(author),
          ]}
        />
        <MainHeader />
        <article>
          <Page>
            <AuthorProfileHeader>
              <div className="headshot">
                <ImageInContext alt={author.name} src={get(author, 'image.image.file.url')} />
              </div>
              <H2 as="h1">{author.name}</H2>
              <H5 as="h2">{author.role}</H5>
              {get(author, 'biography.biography') != null && <P>{author.biography.biography}</P>}
              <Small><A href={`/authors/`}>View All Authors</A></Small>
            </AuthorProfileHeader>
            <HR />
            <div>
              {author.learnArticles.map((article) => (
                <ArticleTile key={article.id}>
                  {(article.articleCategorys || []).length > 0 && <div>
                    {Array.prototype.map.call((article.articleCategorys || []), (c, index) => {
                      return c != null ? (
                        <Label key={index}>
                          <a href={`/learn/category/${kebabCase(c)}/`}>
                            {startCase(c)}
                          </a>
                        </Label>
                      ) : null;
                    })}
                  </div>}
                  <div>
                    <H3><a href={`/learn/${article.slug}/`}>{article.title}</a></H3>
                    {get(article, 'articleExcerpt.articleExcerpt') != null && <p>{get(article, 'articleExcerpt.articleExcerpt')}</p>}
                    <a href={`/learn/${article.slug}/`}>Read More →</a>
                  </div>
                </ArticleTile>
              ))}
            </div>
          </Page>
        </article>
        <MainFooter />
      </TopLevelLayout>
    );
  }
}

/* eslint no-undef: "off"*/
export const pageQuery = graphql`
  query AuthorProfile($authorId: String) {
    author: contentfulAuthor(id: { eq: $authorId }) {
      name
      role
      image {
        image {
          file {
            url
            fileName
            contentType
          }
        }
      }
      biography {
        biography
      }
      learnArticles: learn_article {
        title
        slug
        articleCategorys
        articleExcerpt {
          articleExcerpt
        }
      }
    }
  }
`;
